import React, { useState, useEffect, useRef } from 'react';
import { FlexGrid, FlexGridCellTemplate, FlexGridColumn } from '@grapecity/wijmo.react.grid';
import { CellType } from '@grapecity/wijmo.grid';
import { FlexGridFilter } from '@grapecity/wijmo.react.grid.filter';
import { IconButton, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TextComponent from 'components/Input/TextComponent';

import { useRestApi } from 'context/RestApiContext';
import { MessageModal } from 'components/modal/MessageModal';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import ReplayIcon from '@mui/icons-material/Replay';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';

// アーカイブ検索モーダル
function TaskArchiveSearchModal(props) {
  const restApi = useRestApi();
  const gridRef = useRef(null);

  const [show, setShow] = useState(false);
  // メッセージダイアログ
  const [dialog, setDialog] = useState({});

  // アーカイブ一覧情報
  const [archives, setArchives] = useState([]);

  // 検索条件
  const [searchCondition, setSearchCondition] = useState({});

  // 検索処理
  const searchArchives = (condition) => {
    restApi.post('/api/task/archive/search', condition, (data) => {
      setArchives(data.archives);
    });
  };

  useEffect(() => {
    if (props?.params?.show) {
      setShow(true);
      setArchives([]);
      // 3か月前の日付を設定
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 3);
      setSearchCondition({
        NAME: '',
        ST_DATE: startDate.toISOString().split('T')[0],
        ED_DATE: '',
      });
    }
  }, [props.params]);

  // 編集処理
  const handleChange = (prop) => (target) => {
    setSearchCondition({ ...searchCondition, [prop]: target?.value ?? target?.target.value });
  };

  // 検索ボタン
  const handleOnClickSearch = () => {
    searchArchives(searchCondition);
  };

  // 復元を選択
  const handleClickRestore = (item) => {
    if (item.TYPE == 'カード') {
      setDialog({
        type: 'confirm',
        message: [`カード「${item.TITLE}」を復元します。よろしいですか？`],
        okCallBack: () => {
          restApi.post(`/api/task/cards/unarchive/${item.LIST_ID}/${item.CARD_ID}`, {}, (_) => {
            searchArchives(searchCondition);
          });
        },
      });
    } else if (item.TYPE == 'リスト') {
      setDialog({
        type: 'confirm',
        message: [`リスト「${item.LIST_NAME}」を復元します。よろしいですか？`],
        okCallBack: () => {
          restApi.post(`/api/task/lists/unarchive/${item.LIST_ID}`, {}, (_) => {
            searchArchives(searchCondition);
          });
        },
      });
    }
  };

  // 削除を選択
  const handleClickDelete = (item) => {
    if (item.TYPE == 'カード') {
      setDialog({
        type: 'confirm',
        message: [
          `カード「${item.TITLE}」を削除します。よろしいですか？`,
          `※元に戻すことはできません`,
        ],
        okCallBack: () => {
          restApi.post(`/api/task/cards/${item.CARD_ID}`, { DEL_FLG: '1' }, (_) => {
            searchArchives(searchCondition);
          });
        },
      });
    } else if (item.TYPE == 'リスト') {
      setDialog({
        type: 'confirm',
        message: [
          `リスト「${item.LIST_NAME}」を削除します。よろしいですか？`,
          `※元に戻すことはできません`,
        ],
        okCallBack: () => {
          restApi.post(`/api/task/lists/${item.LIST_ID}`, { DEL_FLG: '1' }, (_) => {
            searchArchives(searchCondition);
          });
        },
      });
    }
  };

  // 閉じる処理
  const handleClose = () => {
    props.loadCards();
    setShow(false);
  };

  return (
    <>
      <Dialog
        sx={{
          '& .MuiDialog-container': {
            alignItems: 'flex-start',
            '& .MuiPaper-root': {
              width: '100%',
              maxWidth: '1240px',
              backgroundColor: '#f1f2f4',
              marginTop: '40px',
            },
          },
        }}
        open={show}
        onClose={(e, reason) => {
          if (reason === 'backdropClick') {
            handleClose();
          }
        }}
      >
        <DialogTitle>
          アーカイブ検索
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Card style={{ height: 720 }}>
            <Row>
              <Col className="pr-1" md="4">
                <label>名称</label>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <TextComponent
                    placeholder="部分一致で検索します"
                    type="text"
                    value={searchCondition.NAME}
                    onChange={handleChange('NAME')}
                  ></TextComponent>
                </div>
              </Col>
              <Col className="pr-1" md="4">
                <label>期間</label>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <TextComponent
                    type="date"
                    value={searchCondition.ST_DATE}
                    onChange={handleChange('ST_DATE')}
                  ></TextComponent>
                  &nbsp;～&nbsp;
                  <TextComponent
                    type="date"
                    value={searchCondition.ED_DATE}
                    onChange={handleChange('ED_DATE')}
                  ></TextComponent>
                  <Button
                    className="ml-2 btn-fill"
                    style={{ width: 80 }}
                    variant="success"
                    size="sm"
                    onClick={handleOnClickSearch}
                  >
                    検索
                  </Button>
                </div>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <div>
                  <FlexGrid
                    ref={gridRef}
                    isReadOnly={true}
                    itemsSource={archives}
                    selectionMode={'Row'}
                    headersVisibility={'Column'}
                    style={{ height: '100%' }}
                  >
                    <FlexGridFilter />
                    <FlexGridColumn binding="TYPE" header="種類" width={120}></FlexGridColumn>
                    <FlexGridColumn
                      binding="LIST_NAME"
                      header="リスト"
                      width={'*'}
                    ></FlexGridColumn>
                    <FlexGridColumn binding="TITLE" header="カード" width={'*'}></FlexGridColumn>
                    <FlexGridColumn
                      binding="ARCHIVE_DATE"
                      header="アーカイブ日時"
                      width={165}
                    ></FlexGridColumn>
                    <FlexGridColumn header="" binding="" isReadOnly={true} width={142}>
                      <FlexGridCellTemplate
                        cellType="Cell"
                        template={(cell) => (
                          <>
                            <Button
                              className="btn-fill p-1"
                              size="sm"
                              variant="secondary"
                              onClick={() => {
                                handleClickRestore(cell.item);
                              }}
                            >
                              <ReplayIcon className="mr-1" fontSize="small" />
                              復元
                            </Button>
                            <Button
                              className="btn-fill p-1 ml-2"
                              size="sm"
                              variant="danger"
                              onClick={() => {
                                handleClickDelete(cell.item);
                              }}
                            >
                              <DeleteForeverIcon className="mr-1" fontSize="small" />
                              削除
                            </Button>
                          </>
                        )}
                      />
                    </FlexGridColumn>
                  </FlexGrid>
                </div>
              </Col>
            </Row>
          </Card>
        </DialogContent>
      </Dialog>
      {/** メッセージダイアログ */}
      <MessageModal dialog={dialog}></MessageModal>
    </>
  );
}
export default TaskArchiveSearchModal;
